<template>
  <div class="p-4 max-w-xl mx-auto" v-if="!isLoading">
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg />
    </div>

    <div class="pb-6 flex flex-col justify-center items-center">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Update Profile
      </h2>
    </div>

    <form @submit.prevent="submitProfile">
      <div
        class="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl shadow-sm"
      >
        <!-- Profile Image -->
        <div class="relative w-28 h-28">
          <img
            class="w-28 h-28 object-cover rounded-full border-2 border-orange-400"
            :src="previewUrl || profileImageUrl"
            alt="Profile"
          />

          <label
            for="profileUpload"
            class="absolute bottom-0 right-0 bg-orange-400 text-white rounded-full p-2 cursor-pointer hover:bg-orange-500 transition shadow-lg"
            title="Change Profile"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 7h4l2-3h6l2 3h4a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1z"
              />
              <circle
                cx="12"
                cy="13"
                r="3"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </label>
          <input
            type="file"
            id="profileUpload"
            accept="image/*"
            @change="onFileChange"
            class="hidden"
          />
        </div>

        <!-- User Name -->
        <h3 class="text-lg font-semibold text-gray-800 text-center truncate">
          {{ $store.state.userLogged }}
        </h3>
      </div>

      <div class="mt-10 mb-6 space-y-3 mx-3">
        <div class="space-y-2">
          <h4 class="text-xs text-zinc-600">Email Address</h4>
          <input
            type="email"
            v-model="email"
            placeholder="Enter your email"
            class="w-full text-sm font-medium rounded-2xl px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 transition duration-200"
            required
          />
        </div>
        <div class="space-y-2">
          <h4 class="text-xs text-zinc-600">Contact Number</h4>
          <input
            type="text"
            v-model="contactnumber"
            placeholder="Enter your contact number"
            class="w-full text-sm font-medium rounded-2xl px-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 transition duration-200"
            required
          />
        </div>
      </div>

      <button
        type="submit"
        class="w-full py-2 bg-orange-400 text-white rounded-xl hover:bg-orange-500 transition"
      >
        <span v-if="!loading">Save Changes</span>
        <span v-else>
          <svg
            class="animate-spin h-5 w-5 mr-2 text-white inline-block"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            ></path>
          </svg>
          Updating...
        </span>
      </button>

      <div
        v-if="isAlert"
        class="fixed top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-3 rounded-2xl shadow-xl text-sm font-medium flex items-center gap-2 animate-fade backdrop-blur-sm border border-green-400/40"
      >
        <!-- Check Icon -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>

        Successfully Updated
      </div>
    </form>
  </div>

  <div v-else class="flex items-center justify-center h-48">
    <svg
      class="w-10 h-10 animate-spin text-orange-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
      ></circle>
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      ></path>
    </svg>
  </div>
</template>

<script>
import { ref, reactive, computed, inject, toRefs } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";

export default {
  name: "UpdateProfile",
  components: { BackSvg },
  setup() {
    const isLoading = ref(false);
    const store = inject("$store");
    const axios = inject("$axios");
    const isAlert = ref(false);
    const loading = ref(false);
    const formData = reactive({
      email: store.state.userEmail,
      contactnumber: store.state.userContact,
      profilephoto: null, // store selected file
    });

    const previewUrl = ref(""); // temporary preview
    const profileImageUrl = computed(() =>
      localStorage.getItem("patientProfile")
    );

    const onFileChange = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      formData.profilephoto = file; // store the file for backend

      const reader = new FileReader();
      reader.onload = (e) => {
        previewUrl.value = e.target.result; // update preview
      };
      reader.readAsDataURL(file);
    };

    const submitProfile = async () => {
      try {
        loading.value = true;
        const data = new FormData();
        data.append("email", formData.email);
        data.append("contactnumber", formData.contactnumber);

        if (formData.profilephoto) {
          data.append("file", formData.profilephoto); // Must match Multer field
        }

        const result = await axios.put(
          `/api/patient/account/general/${store.state.userControlNumber}`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        isAlert.value = true;
        console.log(result);
        store.state.userEmail = formData.email;
        store.state.userContact = formData.contactnumber;

        // optional: update local preview
        if (formData.profilephoto) {
          localStorage.setItem("patientProfile", previewUrl.value);
        }
        loading.value = false;

        setTimeout(() => {
          isAlert.value = false;
        }, 6000);
      } catch (error) {
        console.error("Failed to update profile:", error);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      ...toRefs(formData),
      previewUrl,
      profileImageUrl,
      isLoading,
      onFileChange,
      submitProfile,
      isAlert,
      loading,
    };
  },
};
</script>
